import { useNavigate } from 'react-router-dom';
import { STAGE_CLASSES, STAGE_LABELS } from '../../contacts/types';
import { useOutreachDue } from '../hooks/useOutreach';
import type { DueContact } from '../types';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

/** Most overdue first; never-contacted prospects lead, since they are due by definition. */
const overdueBy = (contact: DueContact) =>
  contact.daysSinceLastSend === null
    ? Number.POSITIVE_INFINITY
    : contact.daysSinceLastSend - contact.effectiveCadenceDays;

/**
 * Contacts due a follow-up today. osprey-api computes "due" from each contact's effective
 * cadence (per-contact override → per-stage cadence → 7-day default) against its last
 * recorded send, and drops SIGNED / ACTIVE_MERCHANT / LOST. Rows open the contact, where the
 * message is drafted and the send recorded — which takes the contact back off this list.
 */
export function FollowUpQueuePanel() {
  const navigate = useNavigate();
  const { data: due = [], isLoading, error } = useOutreachDue();
  const sorted = [...due].sort((a, b) => overdueBy(b) - overdueBy(a));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Prospects whose last message is older than their follow-up cadence, plus anyone never
        contacted. Recording a send on the contact removes them from this list.
      </p>

      {error && <p className="text-sm text-red-500">Could not load the follow-up queue.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !error && sorted.length === 0 && (
        <p className="text-sm text-muted-foreground">Nobody is due a follow-up. 🎉</p>
      )}

      {sorted.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Stage</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Last sent</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Cadence</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((contact) => {
                  const overdue = overdueBy(contact);
                  return (
                    <tr
                      key={contact.contactId}
                      onClick={() => navigate(`/contacts/${contact.contactId}`)}
                      className="border-b border-border hover:bg-muted cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium text-foreground">
                        {contact.name}
                        {contact.businessName && (
                          <span className="block text-xs text-muted-foreground">
                            {contact.businessName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_CLASSES[contact.stage]}`}
                        >
                          {STAGE_LABELS[contact.stage]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {contact.lastSentAt ? formatDate(contact.lastSentAt) : 'Never'}
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        Every {contact.effectiveCadenceDays} day
                        {contact.effectiveCadenceDays === 1 ? '' : 's'}
                      </td>
                      <td className="px-4 py-3 text-foreground">
                        {Number.isFinite(overdue)
                          ? overdue <= 0
                            ? 'Due today'
                            : `${overdue} day${overdue === 1 ? '' : 's'}`
                          : 'First touch'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
